/** Fields the inbox query returns that decide whether a desktop alert is due. */
export type DesktopNotificationCandidate = {
  recipientId: string;
  eventId: string;
  title: string;
  body: string;
  deepLink: string;
  createdAt: number;
  readAt: number | null;
  browserPush: boolean;
};

export type DesktopNotificationDiffState = {
  scope: string;
  // Newest createdAt in the first page seen for this scope; null if empty.
  watermark: number | null;
  seen: readonly string[];
};

const SEEN_LIMIT = 500;

/**
 * Picks inbox items that arrived after this page started listening. The
 * first page for a scope (workspace) is the backlog: it is recorded, never
 * forwarded. Later items qualify only when unseen, newer than that backlog,
 * unread and eligible for push (browserPush, which the server derives from
 * workspace, thread mute and follow preferences).
 */
export function diffDesktopNotifications(
  previous: DesktopNotificationDiffState | null,
  scope: string,
  page: readonly DesktopNotificationCandidate[]
): {
  state: DesktopNotificationDiffState;
  fresh: DesktopNotificationCandidate[];
} {
  if (!previous || previous.scope !== scope) {
    const newest = page.reduce<number | null>(
      (max, item) =>
        max === null || item.createdAt > max ? item.createdAt : max,
      null
    );
    return {
      state: {
        scope,
        watermark: newest,
        seen: page.map((item) => item.recipientId).slice(0, SEEN_LIMIT),
      },
      fresh: [],
    };
  }
  const seen = new Set(previous.seen);
  const fresh = page
    .filter(
      (item) =>
        !seen.has(item.recipientId) &&
        (previous.watermark === null || item.createdAt > previous.watermark) &&
        item.readAt === null &&
        item.browserPush
    )
    .sort((a, b) => a.createdAt - b.createdAt);
  const added = page
    .map((item) => item.recipientId)
    .filter((id) => !seen.has(id));
  return {
    state: {
      scope,
      watermark: previous.watermark,
      seen: [...added, ...previous.seen].slice(0, SEEN_LIMIT),
    },
    fresh,
  };
}
